/**
 * Voice Memo Analysis Panel Component
 * Displays analysis content in SecondaryPanel
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { SecondaryPanelContent, SecondaryPanelOpenButton } from '@/components/layout/secondary-panel-content';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { fetchWithAuth } from '@/lib/api';
import { useInternalMarkdownLinkClick } from '@/lib/internal-link-navigation';
import type { VoiceMemo } from '@/lib/api';

interface VoiceMemoAnalysisPanelProps {
  memo: VoiceMemo;
  onClose: () => void;
  onOpenInEditor?: (filePath: string) => void;
}

export function VoiceMemoAnalysisPanel({
  memo,
  onClose,
  onOpenInEditor
}: VoiceMemoAnalysisPanelProps) {
  const [analysisContent, setAnalysisContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onInternalLinkClick = useInternalMarkdownLinkClick(
    memo.transcript?.path ?? undefined
  );

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!memo.filename) {
        setError('Voice memo filename not available');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Use the API endpoint to get analysis (stored in metadata, not as separate files)
        const encodedFilename = encodeURIComponent(memo.filename);
        const response = await fetchWithAuth(`/api/voice-memos/analysis/${encodedFilename}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('Analysis not available. Generate analysis to view.');
          } else {
            setError(`Failed to load analysis: ${response.statusText}`);
          }
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        // Analysis is stored as an object with various fields
        // Format it for display
        if (data.analysis) {
          // If analysis is already a string (markdown), use it directly
          if (typeof data.analysis === 'string') {
            setAnalysisContent(data.analysis);
          } else {
            // Otherwise, format the analysis object as markdown
            const analysis = data.analysis;
            let markdown = '';

            if (analysis.summary) {
              markdown += `## Summary\n\n${analysis.summary}\n\n`;
            }

            if (analysis.category) {
              markdown += `## Category\n\n${analysis.category}\n\n`;
            }

            if (analysis.keywords && analysis.keywords.length > 0) {
              markdown += `## Keywords\n\n${analysis.keywords.map((k: string) => `- ${k}`).join('\n')}\n\n`;
            }

            if (analysis.actionItems && analysis.actionItems.length > 0) {
              markdown += `## Action Items\n\n${analysis.actionItems.map((item: any) => {
                const text = typeof item === 'string' ? item : item.text || item;
                return `- ${text}`;
              }).join('\n')}\n\n`;
            }

            if (analysis.filingRecommendation) {
              markdown += `## Filing Recommendation\n\n${JSON.stringify(analysis.filingRecommendation, null, 2)}\n\n`;
            }

            if (analysis.clientContext) {
              markdown += `## Client Context\n\n${JSON.stringify(analysis.clientContext, null, 2)}\n\n`;
            }

            setAnalysisContent(markdown || 'No analysis content available.');
          }
        } else {
          setError('Analysis data is empty');
        }
      } catch (err) {
        console.error('Failed to load analysis:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analysis');
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalysis();
  }, [memo.filename]);

  const handleOpenInEditor = () => {
    // Analysis is stored in metadata, not as a separate file
    // Open the transcript file instead, which contains the analysis in frontmatter
    if (memo.transcript?.path && onOpenInEditor) {
      let transcriptPath = memo.transcript.path;

      // Extract relative path if it's absolute
      const contentRootMatch = transcriptPath.match(/\/inbox\/voice-memos\/(.+)$/);
      if (contentRootMatch) {
        transcriptPath = `inbox/voice-memos/${contentRootMatch[1]}`;
      } else if (transcriptPath.startsWith('/')) {
        const parts = transcriptPath.split('/');
        const inboxIndex = parts.indexOf('inbox');
        if (inboxIndex >= 0) {
          transcriptPath = parts.slice(inboxIndex).join('/');
        }
      }

      // Open transcript file (analysis is in metadata/frontmatter)
      onOpenInEditor(transcriptPath);
    }
  };

  return (
    <SecondaryPanelContent
      title={`Analysis: ${memo.filename.replace(/\.(m4a|wav|m4b)$/, '')}`}
      onClose={onClose}
      loading={isLoading}
      error={error}
      headerActions={
        analysisContent ? (
          <SecondaryPanelOpenButton
            onClick={handleOpenInEditor}
          />
        ) : undefined
      }
    >
      {analysisContent ? (
        <div className="p-6">
          <div
            style={{
              animation: 'slideDownStagger 0.4s ease-out 0s both'
            }}
          >
            <MarkdownPreview
              content={analysisContent}
              className="max-w-none"
              compact
              onInternalLinkClick={onInternalLinkClick}
            />
          </div>
        </div>
      ) : null}
    </SecondaryPanelContent>
  );
}

